'use client'

import Link from 'next/link'
import {
  CSSProperties,
  ReactNode,
  createContext,
  useCallback,
  useState,
} from 'react'

/** True while the wrapping card link is hovered or focused. */
export const CatHoverContext = createContext(false)

type CatHoverLinkProps = {
  href: string
  className?: string
  style?: CSSProperties
  children: ReactNode
}

/**
 * The link wrapping a cat tile.
 *
 * The hover/focus state lives here rather than on the <img>: the link is the
 * focusable element and focus does not reach its children, so with the handlers
 * on the image keyboard users never saw the animation. <CatImage> reads the
 * state back out of the context.
 */
export default function CatHoverLink({
  href,
  className,
  style,
  children,
}: CatHoverLinkProps) {
  const [hovered, setHovered] = useState(false)

  const animate = useCallback(() => setHovered(true), [])
  const stopAnimating = useCallback(() => setHovered(false), [])

  return (
    <Link
      href={href}
      className={className}
      style={style}
      onMouseEnter={animate}
      onMouseLeave={stopAnimating}
      onFocus={animate}
      onBlur={stopAnimating}
    >
      <CatHoverContext.Provider value={hovered}>
        {children}
      </CatHoverContext.Provider>
    </Link>
  )
}
